/**
 * LinkedIn ad-click bridge (`li_fat_id`).
 *
 * With enhanced conversion tracking enabled in LinkedIn Campaign Manager — but
 * the Insight Tag *not* installed — LinkedIn appends `li_fat_id` to the landing
 * URL on every ad click and sets no cookie of its own. The value lives only in
 * the URL, so capturing it is on us.
 *
 * The capture has to survive a hop the existing `attribution.ts` deliberately
 * does not cross: the ad lands on the marketing site (`www.`) but the
 * conversion — a waitlist join or signup — happens on the product app (`app.`),
 * a separate origin. `sessionStorage`/`localStorage` are per-origin and would
 * be invisible on `app.`. A cookie scoped to the registrable domain the
 * `app.`/`www.`/apex hosts share (e.g. `movegoalpost.com`) does cross it — the
 * same mechanism the theme preference uses (see useTheme.tsx). So this is a
 * cookie, not storage.
 *
 * It bridges anonymous click → conversion *same-browser only*, the inherent
 * scope of a li_fat_id-only approach: the id rides in the cookie we set on the
 * landing click, so a conversion in another browser or device — where the
 * cookie isn't present — carries no li_fat_id and isn't reported. For the
 * same-browser conversions we do send, LinkedIn resolves the li_fat_id to the
 * member who clicked and attributes on its end.
 *
 * Capture (writing the cookie) is wired only on the marketing site `www.` — the
 * ad target, where the URL carries `li_fat_id`. The product app `app.` does not
 * capture; it consumes the cookie at conversion via `readLiFatId()`. Both
 * functions live here so the module stays symmetric across the two apps: if ads
 * ever point straight at `app.`, wiring `captureLiFatId()` into its `main.tsx`
 * is the only change.
 *
 * See docs/martech.md.
 */

import { sharedCookieDomain } from './cookie-domain.ts';

// The bridge cookie, shared across app./www./apex like `gp_theme`.
const COOKIE_NAME = 'gp_li_fat_id';

// LinkedIn's first-party ad-tracking id, as it appears on the landing URL.
const LI_FAT_ID_PARAM = 'li_fat_id';

// CAPI attributes within a ~90-day click lookback. Past that a `li_fat_id` can
// no longer attribute, so we both expire the cookie near the window edge and,
// on read, drop a value older than it.
const CLICK_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;
const COOKIE_MAX_AGE_SECONDS = CLICK_WINDOW_MS / 1000;

// What we stash in the cookie and hand back at conversion time.
interface LiFatBridge {
  // LinkedIn's li_fat_id, sent verbatim server-side as the
  // LINKEDIN_FIRST_PARTY_ADS_TRACKING_UUID match identifier.
  liFatId: string;
  // Last-click capture time, epoch ms — the click-window backstop.
  capturedAt: number;
}

/**
 * The `li_fat_id` on the current URL, or null. LinkedIn appends it only on an
 * ad-click landing; an organic or in-app load (clean URL) has none. This is the
 * "is this pageload an ad landing" signal — distinct from `readLiFatId()`, which
 * returns any in-window capture from the cookie regardless of how the page was
 * reached.
 */
export function liFatIdFromUrl(): string | null {
  return new URLSearchParams(window.location.search).get(LI_FAT_ID_PARAM);
}

/**
 * On landing, lift `li_fat_id` off the URL and (re)write the bridge cookie.
 * Last-click wins: we overwrite only when the current URL actually carries a
 * `li_fat_id`, so an ordinary in-app navigation — clean URL — leaves an earlier
 * capture intact. Idempotent and safe to call on every pageload; no-ops when
 * there's nothing to capture.
 */
export function captureLiFatId(): void {
  const liFatId = liFatIdFromUrl();
  if (!liFatId) return;

  writeBridgeCookie({ liFatId, capturedAt: Date.now() });
}

/**
 * Read the bridge for the conversion request body, or null when there's nothing
 * usable. Returns null for a value captured outside the ~90-day click window —
 * past it the `li_fat_id` can't attribute, so there's no point sending it.
 * Defensive: a malformed cookie reads as null rather than throwing.
 *
 * Wired into the signup/waitlist submit path in the carry step (see
 * docs/martech.md); exported now so that step is a one-line call.
 */
export function readLiFatId(): LiFatBridge | null {
  const bridge = readBridgeCookie();
  if (!bridge) return null;
  if (Date.now() - bridge.capturedAt > CLICK_WINDOW_MS) return null;
  return bridge;
}

function writeBridgeCookie(bridge: LiFatBridge): void {
  try {
    const value = encodeURIComponent(JSON.stringify(bridge));
    const parts = [
      `${COOKIE_NAME}=${value}`,
      'Path=/',
      `Max-Age=${COOKIE_MAX_AGE_SECONDS}`,
      'SameSite=Lax',
    ];
    const domain = sharedCookieDomain();
    if (domain) parts.push(`Domain=${domain}`);
    if (window.location.protocol === 'https:') parts.push('Secure');
    document.cookie = parts.join('; ');
  } catch {
    // Best-effort: a cookie write failure (sandboxed context, quota) must never
    // break the page or the conversion that rides on it.
  }
}

function readBridgeCookie(): LiFatBridge | null {
  try {
    const prefix = `${COOKIE_NAME}=`;
    const raw = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith(prefix))
      ?.slice(prefix.length);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(decodeURIComponent(raw));
    if (!parsed || typeof parsed !== 'object') return null;

    // Re-validate every field on read (not just on write): a polluted cookie
    // then can't inject a bogus identifier or timestamp into the conversion
    // payload.
    let liFatId: string | null = null;
    let capturedAt: number | null = null;
    for (const [key, value] of Object.entries(parsed)) {
      if (key === 'liFatId' && typeof value === 'string' && value) {
        liFatId = value;
      } else if (key === 'capturedAt' && typeof value === 'number' && Number.isFinite(value)) {
        capturedAt = value;
      }
    }

    if (!liFatId || capturedAt === null) return null;
    return { liFatId, capturedAt };
  } catch {
    return null;
  }
}
